import { useCallback, useEffect, useState, useMemo } from 'react';
import { trackAction } from 'utils/trackAction';
import { Button, makeStyles, Hidden, useMediaQuery } from '@material-ui/core';
import Calendar, { CalendarItemType } from './Calendar';

import ForecastModal from './ForecastModal';

import moment from 'moment-timezone';
import { UserDto } from 'utils/wemble-api.generated';
import { useCreateForecastMutation, useUpdateStatusMutation, useDeleteForecastMutation, useGetCurrentUserQuery, useGetForecastsQuery, useUpdateForecastMutation } from 'utils/wemble-api';
import organization from 'utils/organization';
import Modal from 'components/shared/Modal';
import { cleanedDateString, getToday, statusTitle } from 'components/Home/Forecast/utils';
import { addWeekdays, countWeekdaysInclusive } from './utils/calendarPositioning';
import { getStatusSource } from 'utils/getStatusSource';

const useStyles = makeStyles((theme) => ({
  user_button: {
    position: 'absolute',
    width: '150px',
    height: '40px',
    left: '36px',
    marginTop: '10px',
    [theme.breakpoints.down('sm')]: {
      top: '52px',
      left: '16px',
      width: '120px',
      height: '30px',
    },
  },
}));

const titleRank = (companyId: string, title: string) => {
  if (companyId === '5da0c1370b30f993a68d898f') { // Hannes Snellman
    return {
      'Senior Adviser': 16,
      'Specialist Partner': 15,
      'Specialist Partner, Head of Public Procurement': 15,
      'Counsel': 14,
      'Counsel, Head of Public Procurement': 13,
      'Partner (Elect)': 12.5,
      'Managing Associate': 12,
      'Senior Associate': 11,
      'Associate': 10,
      'Junior Associate': 9,
      'Associate Trainee': 8,
      'Trainee': 7,
      'Thesis Trainee': 6.8,
      'Supervising Legal Assistant': 6.5,
      'Senior Legal Assistant': 6,
      'Executive Assistant': 5,
      'Legal Assistant': 4,
      'Legal Assistant Trainee': 3,
      'Legal AI Analyst': 2,
      'Senior Partner': 1,
      'Managing Partner': 0,
      '': 0
    }[title] || 0;
  }

  if (companyId === '5e4d42f50fc8ec2139341a39') { // Selmer
    const normalizedTitle = (title || '').toLowerCase();
    // Ranking: higher number = appears first (junior roles at top)
    if (normalizedTitle.includes('fullmektig') || normalizedTitle.includes('advokat / associate')) return 40; // Fullmektig/Associate at top
    if (normalizedTitle.includes('fast advokat')) return 30;
    if (normalizedTitle.includes('senioradvokat') || normalizedTitle.includes('senior lawyer')) return 20;
    if (normalizedTitle.includes('partner')) return 10; // Partners
    return 0; // Everything else at bottom
  }

  return 0;
};

const VacationPlanner = ({
  isOpen,
  close,
  selectedUser,
  users,
}: {
  isOpen: boolean;
  close: () => void;
  selectedUser?: string | null | undefined;
  users: UserDto[] | undefined;
}) => {
  const classes = useStyles();
  const startDate = useMemo(() => moment(getToday()), []);
  const [itemModalUserId, setItemModalUserId] = useState<string>();
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItemModal, setEditItemModal] = useState<false | CalendarItemType>(false);

  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allForecasts } = useGetForecastsQuery({}, { refetchOnReconnect: true });

  useEffect(() => {
    if (isOpen) trackAction('openCalendar');
  }, [isOpen]);

  const [createForecast, { isLoading: creatingForecast }] = useCreateForecastMutation();
  const [updateForecast, { isLoading: updatingForecast }] = useUpdateForecastMutation();
  const [deleteForecast, { isLoading: deletingForecast }] = useDeleteForecastMutation();
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateStatusMutation();
  const disabled = creatingForecast || updatingForecast || deletingForecast || updatingStatus;

  const newForecast = useCallback(
    async (status: string, startDate: Date | null, endDate: Date | null, comment: string) => {
      if (!startDate || !itemModalUserId) return;

      await createForecast({
        userId: itemModalUserId,
        forecastUpsertParams: {
          comment: comment || null,
          startDate: cleanedDateString(startDate),
          endDate: cleanedDateString(endDate) || cleanedDateString(startDate),
          status: status as "0" | "1" | "2" | "3" | "away",
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        },
      });
      setAddItemModal(false);
    },
    [createForecast, itemModalUserId],
  );

  const editForecast = useCallback(
    async (status: string, startDate: Date | null, endDate: Date | null, comment: string, forecastId: string) => {
      if (!forecastId || !itemModalUserId || !startDate) return;
      await updateForecast({
        forecastId: forecastId,
        userId: itemModalUserId,
        forecastUpsertParams: {
          comment: comment || null,
          startDate: cleanedDateString(startDate),
          endDate: cleanedDateString(endDate) || cleanedDateString(startDate),
          status: status as "0" | "1" | "2" | "3" | "away",
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        },
      });
      setEditItemModal(false);
    },
    [updateForecast, itemModalUserId],
  );

  const removeForecast = useCallback(
    async (forecastId: string) => {
      if (!forecastId || !itemModalUserId) return;
      await deleteForecast({ forecastId: forecastId, userId: itemModalUserId, sessionId: (window as any)._sessionId });
      setEditItemModal(false);
    },
    [deleteForecast, itemModalUserId],
  );

  const removeWfhAway = useCallback(
    async (_itemId: string) => {
      if (!editItemModal || editItemModal.wfhIndex == null || !itemModalUserId) return;
      const token = localStorage.getItem('token');
      await fetch(`/api/v2/workplace-forecast/${itemModalUserId}/${editItemModal.wfhIndex}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ option: null }),
      });
      setEditItemModal(false);
    },
    [editItemModal, itemModalUserId],
  );

  const handleCloseItemModal = useCallback(() => {
    setAddItemModal(false);
    setEditItemModal(false);
    setItemModalUserId(undefined);
    setDragDates(null);
  }, []);

  const handleOpenEditModal = useCallback((item: CalendarItemType, userId: string) => {
    setEditItemModal(item);
    setItemModalUserId(userId);
  }, []);

  const handleOpenAddModal = useCallback((userId: string) => {
    setAddItemModal(true);
    setItemModalUserId(userId);
  }, []);

  const isAdmin = Boolean(currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin);
  const viewOnly = organization(currentUser?.company).absencePlannerviewOnly || false;

  // Memoize forecast lookup by user to avoid O(n*m) filtering on every render
  // Scope forecasts to only the visible users (+ current user)
  const userIds = useMemo(() => {
    const ids = new Set(users?.map(u => u._id).filter(Boolean) || []);
    if (currentUser?._id) ids.add(currentUser._id);
    return ids;
  }, [users, currentUser?._id]);

  const forecasts = useMemo(() =>
    allForecasts?.filter(f => f.user && userIds.has(f.user)) || [],
    [allForecasts, userIds]
  );

  const forecastsByUser = useMemo(() => {
    if (!forecasts.length) return new Map();

    const lookup = new Map<string, typeof forecasts>();
    forecasts.forEach(forecast => {
      if (!forecast.user) return;
      if (!lookup.has(forecast.user)) {
        lookup.set(forecast.user, []);
      }
      lookup.get(forecast.user)!.push(forecast);
    });

    // Sort forecasts for each user once
    lookup.forEach(forecasts => {
      forecasts.sort((a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime());
    });

    return lookup;
  }, [forecasts]);

  // Memoize the user data processing with forecasts
  const processedUsers = useMemo(() => {
    if (!users) return [];

    const today = moment(getToday());

    const result = users
      .filter((user) => user.completed && !user.admin)
      .map((user) => {
        const userForecasts = forecastsByUser.get(user._id!) || [];

        // Check if there's a forecast covering today
        const hasForecastForToday = userForecasts.some(forecast => {
          const startDate = moment(forecast.startDate);
          const endDate = forecast.endDate ? moment(forecast.endDate) : startDate;
          return today.isSameOrAfter(startDate, 'day') && today.isSameOrBefore(endDate, 'day');
        });

        // Map existing forecasts
        let items = userForecasts.map((forecast) => ({
          title: (forecast.status === 'away' && forecast.comment && forecast.comment.length <= 18)
            ? forecast.comment
            : statusTitle(forecast.status),
          comment: forecast.comment,
          startDate: moment(forecast.startDate),
          endDate: forecast.endDate ? moment(forecast.endDate) : moment(forecast.startDate),
          status: forecast.status,
          _id: forecast._id,
          externalSource: forecast.externalSource,
          isCurrentWorkloadPlaceholder: false
        }));

        // Inject WFH planner "away" days as individual calendar items
        const wfh = user.workplaceForecast;
        if (wfh?.length) {
          const cursor = today.clone();
          for (let i = 0; i < wfh.length; i++) {
            if (i > 0) {
              cursor.add(1, 'day');
              while (cursor.isoWeekday() > 5) cursor.add(1, 'day');
            }
            if (wfh[i] !== 2) continue;

            // Skip if a real forecast already covers this date
            const date = cursor.clone();
            const coveredByForecast = userForecasts.some(f => {
              const s = moment(f.startDate);
              const e = f.endDate ? moment(f.endDate) : s;
              return date.isSameOrAfter(s, 'day') && date.isSameOrBefore(e, 'day');
            });
            if (coveredByForecast) continue;

            items.push({
              title: 'Away',
              comment: null,
              startDate: date,
              endDate: date,
              status: 'away',
              _id: `wfh-${i}-${user._id}`,
              externalSource: 'WFH planner',
              isCurrentWorkloadPlaceholder: false,
              wfhIndex: i
            });
          }

          items.sort((a, b) => a.startDate.valueOf() - b.startDate.valueOf());
        }

        // If no forecast for today (including WFH items), inject a virtual one with current workload
        const hasTodayCovered = hasForecastForToday || items.some(item =>
          today.isSameOrAfter(item.startDate, 'day') && today.isSameOrBefore(item.endDate!, 'day')
        );
        if (!hasTodayCovered) {
          const todayStatus = user.onLeave ? 'away' : user.currentWorkload?.toString();
          const virtualItem = {
            title: statusTitle(todayStatus),
            comment: undefined,
            startDate: today.clone(),
            endDate: today.clone(),
            status: todayStatus,
            _id: `virtual-today-${user._id}`,
            externalSource: undefined,
            isCurrentWorkloadPlaceholder: true
          };

          // Insert the virtual item in the correct chronological position
          items = [...items, virtualItem].sort((a, b) =>
            a.startDate.valueOf() - b.startDate.valueOf()
          );
        }

        return {
          isCurrentUser: user._id === currentUser?._id,
          id: user._id,
          name: user.name,
          currentWorkload: user.currentWorkload,
          title: user.title,
          profilePicture: user.profilePicture,
          items
        };
      })
      .sort((a, b) => {
        // Always prioritize current user and selected user first
        if (a.isCurrentUser) return -1;
        if (b.isCurrentUser) return 1;
        if (a.id === selectedUser) return -1;
        if (b.id === selectedUser) return 1;

        // Then sort by title rank if company has custom ranking
        if (currentUser?.company === '5da0c1370b30f993a68d898f' || currentUser?.company === '5e4d42f50fc8ec2139341a39') {
          const rankDiff = titleRank(currentUser.company.toString(), b.title || '') - titleRank(currentUser.company.toString(), a.title || '');
          if (rankDiff !== 0) return rankDiff;
        }

        // Finally, sort alphabetically by name
        return (a.name || '').localeCompare(b.name || '');
      });

    return result;
  }, [users, forecastsByUser, currentUser?._id, currentUser?.company, selectedUser]);

  var viewportLength = 10;
  if (useMediaQuery('(min-width:960px)')) viewportLength += 1
  if (useMediaQuery('(min-width:1100px)')) viewportLength += 1
  if (useMediaQuery('(min-width:1300px)')) viewportLength += 2
  if (useMediaQuery('(min-width:1500px)')) viewportLength += 1
  if (useMediaQuery('(min-width:1700px)')) viewportLength += 2
  if (useMediaQuery('(min-width:1700px)')) viewportLength += 2


  const renderLength = 270;

  const handleForecastSave = useCallback(
    async (status: string, startDate: Date | null, endDate: Date | null, comment: string) => {
      if (editItemModal && editItemModal.isCurrentWorkloadPlaceholder) {
        // When editing a placeholder, check if we should update current workload or create a forecast
        if (!itemModalUserId || !startDate) return;

        const today = moment(getToday());
        const isSingleDayToday = moment(startDate).isSame(today, 'day') &&
          (!endDate || moment(endDate).isSame(today, 'day'));
        const hasComment = comment && comment.length > 0;

        // Only update current workload via updateStatus if:
        // 1. It's the current user (updateStatus has no userId param)
        // 2. It's a single day (start and end are today)
        // 3. AND there's no comment
        // Otherwise, create a forecast
        const isCurrentUser = itemModalUserId === currentUser?._id;
        if (isSingleDayToday && !hasComment && isCurrentUser) {
          // Update current status for today (handles both workload and away)
          if (status === 'away' || (parseInt(status) >= 0 && parseInt(status) <= 3)) {
            await updateStatus({
              type: 'current',
              statusUpdateParams: {
                status: status as '0' | '1' | '2' | '3' | 'away',
                source: getStatusSource()
              },
            });
          }
        } else {
          // Create a new forecast (date range changed or comment added)
          await newForecast(status, startDate, endDate, comment);
        }
      } else if (editItemModal && editItemModal._id) {
        await editForecast(status, startDate, endDate, comment, editItemModal._id);
      } else {
        await newForecast(status, startDate, endDate, comment);
      }
    },
    [editItemModal, editForecast, newForecast, itemModalUserId, users, updateStatus]
  );

  const handleForecastDelete = useCallback(
    async (forecastId: string) => {
      await removeForecast(forecastId);
    },
    [removeForecast]
  );

  const handleDragCreate = useCallback(
    (userId: string, startDate: Date, endDate: Date) => {
      setItemModalUserId(userId);
      setAddItemModal(true);
      // We'll pass the drag dates to the modal through a separate state
      setDragDates({ startDate, endDate });
    },
    []
  );

  const [dragDates, setDragDates] = useState<{ startDate: Date; endDate: Date } | null>(null);

  // Drag-to-move handler
  const handleDragMove = useCallback(
    async (itemId: string, startDate: Date, _endDate: Date) => {
      if (!itemId) return;
      // Find the forecast to get userId and other info
      const forecast = forecasts?.find(f => f._id === itemId);
      if (!forecast) return;
      // Calculate the number of weekdays in the original forecast
      const origStart = moment(forecast.startDate);
      const origEnd = forecast.endDate ? moment(forecast.endDate) : origStart;
      const weekdayCount = countWeekdaysInclusive(origStart, origEnd);
      const newStart = moment(startDate);
      const newEnd = addWeekdays(newStart, weekdayCount);
      await updateForecast({
        forecastId: itemId,
        userId: forecast.user || "",
        forecastUpsertParams: {
          comment: forecast.comment || null,
          startDate: cleanedDateString(newStart.toDate()),
          endDate: cleanedDateString(newEnd.toDate()),
          status: forecast.status as "0" | "1" | "2" | "3" | "away",
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        },
      });
    },
    [updateForecast, forecasts]
  );

  // Drag-to-resize handler
  const handleDragResize = useCallback(
    async (itemId: string, startDate: Date, endDate: Date) => {
      if (!itemId) return;

      // Check if this is a virtual placeholder item
      if (itemId.startsWith('virtual-today-')) {
        // Extract userId from the virtual ID (format: "virtual-today-{userId}")
        const userId = itemId.replace('virtual-today-', '');
        const user = users?.find(u => u._id === userId);
        if (!user) return;

        // Create a new forecast with the resized dates
        const todayStatus = user.onLeave ? 'away' : user.currentWorkload?.toString();
        await createForecast({
          userId: userId,
          forecastUpsertParams: {
            comment: null,
            startDate: cleanedDateString(startDate),
            endDate: cleanedDateString(endDate),
            status: todayStatus as "0" | "1" | "2" | "3" | "away",
            sessionId: (window as any)._sessionId,
            source: getStatusSource()
          },
        });
        return;
      }

      // Handle normal forecast resize
      const forecast = forecasts?.find(f => f._id === itemId);
      if (!forecast) return;
      await updateForecast({
        forecastId: itemId,
        userId: forecast.user || "",
        forecastUpsertParams: {
          comment: forecast.comment || null,
          startDate: cleanedDateString(startDate),
          endDate: cleanedDateString(endDate),
          status: forecast.status as "0" | "1" | "2" | "3" | "away",
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        },
      });
    },
    [updateForecast, forecasts, users, createForecast]
  );

  return (
    <div>
      <ForecastModal
        isOpen={Boolean(addItemModal) || Boolean(editItemModal)}
        item={editItemModal}
        onSave={editItemModal && editItemModal.wfhIndex != null ? undefined : handleForecastSave}
        onDelete={editItemModal && editItemModal.wfhIndex != null
          ? removeWfhAway
          : editItemModal && !editItemModal.isCurrentWorkloadPlaceholder ? handleForecastDelete : undefined}
        disabled={disabled}
        onClose={handleCloseItemModal}
        dragDates={dragDates}
      />
      <Modal customWidth={1700} visible={isOpen} close={() => !disabled && close()} closeButton>
        <div style={{ margin: '-14px' }}>
          {!isAdmin && !viewOnly && (
            <Button
              className={classes.user_button}
              variant="outlined"
              color="primary"
              onClick={() => currentUser?._id && handleOpenAddModal(currentUser._id)}
              disabled={disabled}
            >
              Add forecast
            </Button>
          )}

          <Hidden smUp>
            <br />
          </Hidden>

          <Calendar
            startDate={startDate}
            length={renderLength}
            viewportLength={viewportLength}
            editItem={handleOpenEditModal}
            onDragCreate={handleDragCreate}
            onDragMove={handleDragMove}
            onDragResize={handleDragResize}
            isAdmin={isAdmin}
            viewOnly={viewOnly}
            users={processedUsers}
          />
        </div>
      </Modal>

    </div >
  );
};

export default VacationPlanner;
